import { cn } from "@app/lib/utils";
import { useMemo, useState } from "react";

// --- Standardized Interface ---
export type HistogramItem = {
  id: string;
  amount: number;
  date: string; // ISO string
  label: React.ReactNode; // description or kind (string label or rich element)
};

interface SharedHistogramProps {
  items: HistogramItem[];
  ratio?: number;
  year?: number;
  title?: string;
  colorClass?: string; // Optional: custom color for bars
}

export function SharedHistogram({
  items,
  ratio = 1,
  year = 2025,
  title = "Activity",
  colorClass = "bg-primary", // Default color
}: SharedHistogramProps) {
  // --- 1. State ---
  const [hoveredItem, setHoveredItem] = useState<HistogramItem | null>(null);
  const [lockedItem, setLockedItem] = useState<HistogramItem | null>(null);

  const displayItem = lockedItem ?? hoveredItem;

  // --- 2. Data Processing ---
  const chartData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i,
      monthName: new Date(year, i).toLocaleString("default", {
        month: "short",
      }),
      items: [] as HistogramItem[],
      total: 0,
    }));

    items.forEach((item) => {
      const d = new Date(item.date);
      if (d.getFullYear() === year) {
        const monthIndex = d.getMonth();
        months[monthIndex].items.push(item);
        months[monthIndex].total += Math.abs(item.amount);
      }
    });

    const maxMonthlyTotal = Math.max(...months.map((m) => m.total));

    return { months, maxMonthlyTotal };
  }, [items, year]);

  const { months, maxMonthlyTotal } = chartData;

  // --- 3. Handlers ---
  const handleItemClick = (e: React.MouseEvent, item: HistogramItem) => {
    e.stopPropagation();
    if (lockedItem?.id === item.id) {
      setLockedItem(null);
    } else {
      setLockedItem(item);
    }
  };

  const handleBackgroundClick = () => setLockedItem(null);

  // --- 4. Render ---
  return (
    <div
      className="relative w-[600px] h-[450px] bg-[#F3F3F3] border rounded-b-xl px-8 pt-8 pb-8 flex flex-col items-center justify-center cursor-default"
      onMouseLeave={() => setHoveredItem(null)}
      onClick={handleBackgroundClick}
    >
      {/* Info Card Overlay */}
      <div className="absolute top-0 right-0 w-[460px] h-[100px] z-20 bg-[white] border-l border-l-[4px] border-l-[orange]">
        <div
          className={cn(
            "relative bg-popover text-popover-foreground p-3",
            displayItem
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-2",
            lockedItem ? "ring-1 ring-primary/20" : ""
          )}
        >
          {displayItem ? (
            <div className="flex flex-col gap-1">
              <div>{displayItem.label}</div>
            </div>
          ) : (
            <div className="h-full w-full opacity-0">Empty</div>
          )}
        </div>
      </div>

      {/* Bars */}
      <div className="flex items-end justify-between w-full h-full gap-2 pt-4 pb-8">
        {months.map((month) => {
          const columnHeightPercentage =
            maxMonthlyTotal > 0
              ? (month.total / maxMonthlyTotal) * 100 * ratio
              : 0;

          return (
            <div
              key={month.monthIndex}
              className="relative flex flex-col justify-end h-full w-full group"
            >
              <div
                className="w-full flex flex-col-reverse rounded-t-md overflow-hidden duration-500 ease-in-out bg-slate-100"
                style={{ height: `${columnHeightPercentage}%` }}
              >
                {month.items.map((item, idx) => {
                  const itemHeight =
                    (Math.abs(item.amount) / month.total) * 100;
                  const isLocked = lockedItem?.id === item.id;
                  const isHovered = hoveredItem?.id === item.id;

                  return (
                    <div
                      key={item.id}
                      onMouseEnter={() => setHoveredItem(item)}
                      onClick={(e) => handleItemClick(e, item)}
                      style={{ height: `${itemHeight}%` }}
                      className={cn(
                        "w-full border-b border-white/20 cursor-pointer duration-200 border border-slate-500",
                        colorClass, // Applied dynamic color
                        idx % 2 === 0 ? "opacity-90" : "opacity-80",
                        isHovered && !isLocked && "opacity-70",
                        isLocked &&
                          "!bg-yellow-500 opacity-100 ring-2 ring-yellow-600 z-10",
                        lockedItem &&
                          !isLocked &&
                          !isHovered &&
                          "opacity-40 grayscale-[0.5]"
                      )}
                    />
                  );
                })}
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground uppercase tracking-wider">
                {month.monthName}
              </div>
            </div>
          );
        })}
      </div>
      <div className="w-full h-[40px] z-20 text-sm absolute -bottom-2 left-0 right-0 text-center">
        {title}
      </div>
    </div>
  );
}
